import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Table, Space, Input, Button, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { getLoansByFilters, downloadInvoicePDF, deleteLoan, getConsultants } from '../../services/loanService';
import { LOAN_STATUS } from '../../constants/loanStatus';
import { Link, useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, DownloadOutlined, EditOutlined, ZoomInOutlined, FileExcelOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Page from '../../components/layout/Page';
import LinkWithIcon from '../../components/common/LinkWithIcon';
import * as XLSX from 'xlsx';
import DeleteConfirmationModal from '../../components/loan/DeleteConfirmationModal';


const LoansAdmin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedConsultants, setSelectedConsultants] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [loans, setLoans] = useState([]);
    const [filteredLoans, setFilteredLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [selectedLoanId, setSelectedLoanId] = useState(null);

    const years = Array.from(new Array(5), (val, index) => ({
        value: new Date().getFullYear() - index,
        label: String(new Date().getFullYear() - index)
    }));

    const getCommissionTierDisplay = (loan) => {
        if (!loan.commission_tiers?.length) return '-';
        return loan.commission_tiers.map(tier => 
            `${tier.percentage}% (${formatCurrency(tier.amount)})`
        ).join(' ');
    };

    const handleDelete = async () => {
        try {
            await deleteLoan(selectedLoanId);
            message.success(t('loan.delete.success'));
            setIsDeleteModalVisible(false);
            // Refresh the loans list
            const filters = {
                year: selectedYear
                // No consultant filter to show all loans
            };
            const loansResponse = await getLoansByFilters(filters);
            if (Array.isArray(loansResponse)) {
                const sortedLoans = [...loansResponse].sort((a, b) => 
                    new Date(a.reported_at) - new Date(b.reported_at)
                );
                setLoans(sortedLoans);
                setFilteredLoans(sortedLoans);
            }
        } catch (error) {
            message.error(t('loan.delete.error'));
        }
    };

    // Fetch consultants list  
    const fetchConsultants = async () => {
        try {
            const response = await getConsultants();
            setConsultants(response);
        } catch (error) {
            console.error('Error fetching consultants:', error);
            message.error('Error loading consultants');
        }
    };
      
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const filters = {
                    year: selectedYear
                    // No consultant filter to show all loans
                };
                const loansResponse = await getLoansByFilters(filters);
                if (!loansResponse || !Array.isArray(loansResponse)) {
                    console.error('Unexpected response format:', loansResponse);
                    setLoans([]);
                    setFilteredLoans([]);
                    return;
                }
                const sortedLoans = [...loansResponse].sort((a, b) => 
                    new Date(a.reported_at) - new Date(b.reported_at)
                );
                setLoans(sortedLoans);
                setFilteredLoans(sortedLoans);

                // Calculate metrics
                const totalVolume = sortedLoans.reduce((sum, loan) => sum + parseFloat(loan.evaluation_amount), 0);
                const totalProvision = sortedLoans.reduce((sum, loan) => sum + parseFloat(loan.commission_amount || 0), 0);
                const averageVolume = totalVolume / (sortedLoans.length || 1);
                const signedEvaluationSum = sortedLoans
                    .filter(loan => loan.status === LOAN_STATUS.SIGNED)
                    .reduce((sum, loan) => sum + parseFloat(loan.evaluation_amount), 0);
                const pendingEvaluationSum = sortedLoans
                    .filter(loan => loan.status === LOAN_STATUS.PENDING)
                    .reduce((sum, loan) => sum + parseFloat(loan.evaluation_amount), 0);
                    
            } catch (error) {
                console.error('Error fetching loans:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        fetchConsultants();
    }, [selectedYear]);

    // Global search and status filter function
    useEffect(() => {
        const searchFields = [
            'process_number',
            ['customer', 'name'],
            ['bank', 'name'],
            ['pooler', 'name'],
            ['loan_type', 'name'],
            'detailed_status'
        ];

        const filtered = loans.filter(loan => {
            // Status filter
            if (selectedStatuses.length > 0) {
                const status = loan.detailed_status?.replace('LoanStatus.', '');
                if (!selectedStatuses.includes(status)) {
                    return false;
                }
            }

            // Consultant filter
            if (selectedConsultants.length > 0) {
                if (!selectedConsultants.includes(loan.primary_consultant?.id)) {
                    return false;
                }
            }

            // Text search
            if (!searchText) return true;

            return searchFields.some(field => {
                let value;
                if (Array.isArray(field)) {
                    // Handle nested fields
                    value = loan[field[0]]?.[field[1]];
                } else {
                    value = loan[field];
                }
                return value?.toString().toLowerCase().includes(searchText.toLowerCase());
            });
        });

        setFilteredLoans(filtered);
    }, [searchText, loans, selectedStatuses, selectedConsultants]);

    // Get unique statuses from loans
    const getAvailableStatuses = () => {
        const statuses = new Set(loans.map(loan => loan.detailed_status?.replace('LoanStatus.', '')));
        return Array.from(statuses).filter(Boolean).sort();
    };

    const handleDownloadInvoice = async (loanId) => {
        try {
            await downloadInvoicePDF(loanId);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            message.error('Failed to download invoice PDF');
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDecimal = (value) => {
        if (!value) return '-';
        return new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const handleExportToExcel = () => {
        // Prepare data for export
        const exportData = filteredLoans.map(loan => ({
            [t('common.month')]: loan.reported_at ? formatDate(loan.reported_at).substring(3) : '',
            [t('loan.fields.customer')]: loan.customer?.name || '',
            [t('loan.fields.bank')]: loan.bank?.name || '',
            [t('loan.fields.pooler')]: loan.pooler?.name || '',
            [t('loan.fields.processNumber')]: loan.process_number || '',
            [t('loan.fields.amount')]: loan.amount ? loan.amount.toString().replace('.', ',') : '0',
            [t('loan.fields.signatureDate')]: loan.signature_date ? formatDate(loan.signature_date) : '',
            [t('loan.fields.loanType')]: loan.loan_type?.name || '',
            [t('loan.fields.interestLock')]: loan.interest_lock_in_years ? `${loan.interest_lock_in_years.toString().replace('.', ',')} ${t('common.years')}` : '',
            [t('loan.fields.commission')]: loan.provision ? `${parseFloat(loan.provision).toFixed(3).toString().replace('.', ',')}%` : '',
            [t('loan.fields.evaluationAmount')]: loan.evaluation_amount ? loan.evaluation_amount.toString().replace('.', ',') : '0',
            [t('loan.fields.detailed_status')]: t(`loan.status.consultant.${loan.detailed_status?.replace('LoanStatus.', '')}`) || '',
            [t('loan.fields.primary_share')]: loan.primary_consultant_share ? loan.primary_consultant_share.toString().replace('.', ',') : '0',
            [t('loan.fields.temporary_share')]: loan.temporary_consultant_share ? loan.temporary_consultant_share.toString().replace('.', ',') : '0'
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Loans');

        // Generate filename with current date
        const date = new Date().toISOString().slice(0,10);
        const fileName = `loans_export_${date}.xlsx`;

        // Save file
        XLSX.writeFile(wb, fileName);
    };

    const columns = [
        {
            title: t('common.actions'),
            key: 'actions',
            fixed: 'left',
            width: 150,
            render: (_, record) => (
                <Space size="middle">
                    <Button 
                        onClick={() => navigate(`/loans/${record.id}`)}
                        icon={<ZoomInOutlined />}
                        title="View Details"
                    >
                    </Button>                    
                    <Button 
                        onClick={() => navigate(`/loans/${record.id}/extended-edit`)}
                        icon={<EditOutlined />}
                        title="Edit Loan"
                    >
                    </Button>
                    <Button 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => {
                            setSelectedLoanId(record.id);
                            setIsDeleteModalVisible(true);
                        }}
                    >
                    </Button>                    
                    {record.invoice_number && (
                        <Button 
                            onClick={() => handleDownloadInvoice(record.id)}
                            icon={<DownloadOutlined />}
                            title="Download Invoice"
                        >
                            {t('common.invoice')}
                        </Button>
                    )}
                </Space>
            ),
        },
        {
            title: t('loan.fields.primaryConsultant'),
            dataIndex: ['primary_consultant'],
            key: 'primary_consultant',
            fixed: 'left',
            width: 150,
            render: (consultant) => consultant ? `${consultant.first_name} ${consultant.last_name}` : '-',
            sorter: (a, b) => (a.primary_consultant?.last_name || '').localeCompare(b.primary_consultant?.last_name || '')
        },
        {
            title: t('common.month'),
            dataIndex: 'reported_at',
            key: 'reported_at',
            fixed: 'left',
            width: 100,
            align: 'right',
            render: (text) => text ? formatDate(text).substring(3) : '',
            sorter: (a, b) => new Date(a.reported_at) - new Date(b.reported_at)
        },
        {
            title: t('loan.fields.customer'),
            dataIndex: ['customer', 'name'],
            key: 'customer_name',
            fixed: 'left',
            width: 150,
            render: (text, record) => (
                <Link to={`/customers/${record.customer.id}`}>{text}</Link>
            ),
            sorter: (a, b) => (a.customer?.name || '').localeCompare(b.customer?.name || '')
        },
        {
            title: t('loan.fields.bank'),
            dataIndex: ['bank', 'name'],
            key: 'bank_name',
            fixed: 'left',
            width: 150,
            sorter: (a, b) => (a.bank?.name || '').localeCompare(b.bank?.name || '')
        },
        {
            title: t('loan.fields.pooler'),
            dataIndex: ['pooler', 'name'],
            key: 'pooler_name',
            fixed: 'left',
            width: 150,
            sorter: (a, b) => (a.pooler?.name || '').localeCompare(b.pooler?.name || '')
        },        
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => (
              <LinkWithIcon to={`/loans/${record.id}`}>
                {text}
              </LinkWithIcon>
            )
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (text) => formatCurrency(text),
            sorter: (a, b) => a.amount - b.amount
        },
        {
            title: t('loan.fields.signatureDate'),
            dataIndex: 'signature_date',
            key: 'signature_date',
            align: 'right',
            render: (text) => formatDate(text),
            sorter: (a, b) => new Date(a.signature_date) - new Date(b.signature_date)
        },
        {
            title: t('loan.fields.loanType'),
            dataIndex: ['loan_type', 'name'],
            key: 'loan_type',
            sorter: (a, b) => (a.loan_type?.name || '').localeCompare(b.loan_type?.name || '')
        },
        {
            title: t('loan.fields.interestLock'),
            dataIndex: 'interest_lock_in_years',
            key: 'interest_lock',
            align: 'right',
            render: (text) => text ? `${formatDecimal(text)} ${t('common.years')}` : '-'
        },
        {
            title: t('loan.fields.commission'),
            dataIndex: 'provision',
            key: 'provision',
            align: 'right',
            render: (text) => text ? `${formatDecimal(parseFloat(text).toFixed(3))}%` : '-',
            sorter: (a, b) => a.provision - b.provision
        },
        {
            title: t('loan.fields.evaluationAmount'),
            dataIndex: 'evaluation_amount',
            key: 'evaluation_amount',
            align: 'right',
            render: (text) => formatCurrency(text),
            sorter: (a, b) => a.evaluation_amount - b.evaluation_amount
        },
        {
            title: t('loan.fields.detailed_status'),
            dataIndex: 'detailed_status',
            key: 'detailed_status',
            render: (text) => {
                const status = text?.replace('LoanStatus.', '');
                return status ? t(`loan.status.consultant.${status}`) : '-';
            }
        },
        {
            title: t('loan.fields.commissionTier'),
            dataIndex: 'commission_tiers',
            key: 'commission_tiers',
            align: 'right',
            render: (text, record) => getCommissionTierDisplay(record)
        },
        {
            title: t('loan.fields.commission'),
            dataIndex: 'primary_consultant_share',
            key: 'consultant_share',
            align: 'right',
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    {text ? formatCurrency(text) : '-'}
                    {record.temporary_consultant_share > 0 && (
                        <small style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                            (+{formatCurrency(record.temporary_consultant_share)} {t('loan.fields.holidayReplacement')})
                        </small>
                    )}
                </Space>
            ),
            sorter: (a, b) => (a.primary_consultant_share || 0) - (b.primary_consultant_share || 0)
        }
    ];

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={24} sm={24} md={12} lg={8}>
                <Input
                    placeholder={t('common.search')}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8}>
                <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportToExcel}
                    style={{ width: '100%' }}
                >
                    {t('common.export')}
                </Button>
            </Col>
            <Col xs={12} sm={12} md={8} lg={8}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/loans/new')}
                    style={{ width: '100%' }}
                >
                    {t('loan.addLoan')}
                </Button>
            </Col>
        </Row>
    );

    return (
        <Page
            title={t('loan.title')}
            subtitle={t('loan.subtitleAdmin')}
            headerExtra={headerExtra}
            containerStyle={{ padding: 24 }}    
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Filters */}
                <Row gutter={[16, 16]} align="middle">
                    <Col>
                        <Select
                            value={selectedYear}
                            onChange={setSelectedYear}
                            style={{ width: 120 }}
                        >
                            {years.map(year => (
                                <Select.Option key={year.value} value={year.value}>
                                    {year.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            mode="multiple"
                            placeholder={t('common.statusfilter')}
                            value={selectedStatuses}
                            onChange={setSelectedStatuses}
                            style={{ minWidth: 200 }}
                            allowClear
                            maxTagCount="responsive"
                        >
                            {getAvailableStatuses().map(status => (
                                <Select.Option key={status} value={status}>
                                    {t(`loan.status.consultant.${status}`)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            mode="multiple"
                            placeholder={t('loan.filter.consultant') || 'Filter by Consultant'}
                            value={selectedConsultants}
                            onChange={setSelectedConsultants}
                            style={{ minWidth: 200 }}
                            allowClear
                            maxTagCount="responsive"
                        >
                            {consultants.map(consultant => (
                                <Select.Option key={consultant.id} value={consultant.id}>
                                    {`${consultant.first_name} ${consultant.last_name}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {/* Table */}
                <Table
                    dataSource={filteredLoans}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 2500 }}
                    pagination={{
                        pageSize: 15,
                        showTotal: (total, range) => `${range[0]}-${range[1]} von ${total} Einträgen`
                    }}
                />
            </Space>
            <DeleteConfirmationModal
                visible={isDeleteModalVisible}
                onCancel={() => setIsDeleteModalVisible(false)}
                onConfirm={handleDelete}
            />            
        </Page>
    );
};

export default LoansAdmin;
